import React, { useEffect, useState, useRef } from 'react';
import { collection, query, orderBy, limit, onSnapshot, getDocs, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase/firebase';

const AttendancePreview = ({ projectId, onClick }) => {
  const [siteStatus, setSiteStatus] = useState({
    onSite: [],
    offSite: [],
    groupedByCompany: {},
    groupedByTeam: {},
    latestActivity: null
  });
  const [loading, setLoading] = useState(true);

  // Cache: uid → { userName, role, companyName, teamId, teamName }
  // teamMemberMap: uid → team (built once at setup)
  const userDataCache = useRef({});
  const teamMemberMap = useRef({});

  useEffect(() => {
    if (!projectId) return;

    userDataCache.current = {};
    teamMemberMap.current = {};

    let unsubscribe;

    const setup = async () => {
      // Build uid→team map once — O(N_teams) reads instead of O(N_users × N_teams) per event
      const teamsSnap = await getDocs(collection(db, `projects/${projectId}/teams`));
      const teamsData = teamsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      await Promise.all(teamsData.map(async team => {
        const membersSnap = await getDocs(
          collection(db, `projects/${projectId}/teams/${team.id}/memberDetails`)
        );
        membersSnap.docs.forEach(d => { teamMemberMap.current[d.id] = team; });
      }));

      unsubscribe = onSnapshot(
        collection(db, `projects/${projectId}/siteAccess`),
        async (snapshot) => {
          if (snapshot.empty) {
            setSiteStatus({ onSite: [], offSite: [], groupedByCompany: {}, groupedByTeam: {}, latestActivity: null });
            setLoading(false);
            return;
          }

          // Remove deleted users from cache
          snapshot.docChanges()
            .filter(c => c.type === 'removed')
            .forEach(c => { delete userDataCache.current[c.doc.id]; });

          // Only fetch log for users whose doc actually changed
          const changedUids = snapshot.docChanges()
            .filter(c => c.type !== 'removed')
            .map(c => c.doc.id);

          await Promise.all(changedUids.map(async uid => {
            const cached = userDataCache.current[uid] || {};
            try {
              const logsSnap = await getDocs(query(
                collection(db, `projects/${projectId}/siteAccess/${uid}/logs`),
                orderBy('timestamp', 'desc'),
                limit(1)
              ));
              if (!logsSnap.empty) {
                const log = logsSnap.docs[0].data();
                cached.latestLog = log;
                cached.userName = log.name || uid;
                cached.role = log.role || null;
                cached.logTimestamp = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
                cached.logType = log.type;
              }

              // Team — served from teamMemberMap (no extra read)
              if (!cached.teamFetched) {
                const team = teamMemberMap.current[uid] || null;
                cached.teamId = team?.id || null;
                cached.teamName = team?.name || null;
                cached.teamFetched = true;
              }

              // Company — only fetch once per PM
              if (cached.role === 'pm' && !cached.companyFetched) {
                const auth = getAuth();
                if (uid === auth.currentUser?.uid) {
                  try {
                    const userProfileDoc = await getDoc(doc(db, 'users', uid));
                    if (userProfileDoc.exists()) {
                      const companyId = userProfileDoc.data().companyId;
                      if (companyId) {
                        const companyDoc = await getDoc(doc(db, 'companies', companyId));
                        cached.companyName = companyDoc.exists() ? companyDoc.data().name : 'Unknown Company';
                      }
                    }
                  } catch {
                    cached.companyName = 'Unknown Company';
                  }
                } else {
                  cached.companyName = 'Project Managers';
                }
                cached.companyFetched = true;
              }

              userDataCache.current[uid] = cached;
            } catch (error) {
              console.error('Error fetching user logs:', error);
            }
          }));

          // Rebuild display state from all current docs + cache
          const onSiteUsers = [];
          const offSiteUsers = [];
          let latestActivity = null;

          for (const userDoc of snapshot.docs) {
            const uid = userDoc.id;
            const cached = userDataCache.current[uid];
            if (!cached?.latestLog) continue;

            const userData = userDoc.data();
            if (userData.currentStatus === undefined) continue;

            const userInfo = {
              uid,
              userName: cached.userName,
              role: cached.role,
              companyName: cached.companyName || null,
              teamId: cached.teamId,
              teamName: cached.teamName,
              timestamp: cached.logTimestamp,
              status: userData.currentStatus,
            };

            if (userData.currentStatus) {
              onSiteUsers.push(userInfo);
            } else {
              offSiteUsers.push(userInfo);
            }

            if (!latestActivity || cached.logTimestamp > latestActivity.timestamp) {
              latestActivity = {
                userName: cached.userName,
                timestamp: cached.logTimestamp,
                action: cached.logType === 'checkIn' ? 'in' : 'out',
              };
            }
          }

          const groupedByCompany = {};
          const groupedByTeam = {};
          onSiteUsers.forEach(user => {
            if (user.role === 'pm') {
              const company = user.companyName || 'Unknown Company';
              if (!groupedByCompany[company]) groupedByCompany[company] = [];
              groupedByCompany[company].push(user);
            } else if (user.teamName) {
              if (!groupedByTeam[user.teamName]) groupedByTeam[user.teamName] = [];
              groupedByTeam[user.teamName].push(user);
            }
          });

          setSiteStatus({ onSite: onSiteUsers, offSite: offSiteUsers, groupedByCompany, groupedByTeam, latestActivity });
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching attendance:', error);
          setLoading(false);
        }
      );
    };

    setup();
    return () => { if (unsubscribe) unsubscribe(); };
  }, [projectId]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const checkInDate = new Date(timestamp);
    const isToday = checkInDate.toDateString() === now.toDateString();
    if (isToday) {
      return checkInDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    return checkInDate.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
  };

  if (loading) {
    return (
      <div style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--branding-app-surface-panel-hint)', fontSize: '14px' }}>
        Loading...
      </div>
    );
  }

  const { onSite, offSite, groupedByCompany, groupedByTeam } = siteStatus;
  const sortedCompanyNames = Object.keys(groupedByCompany).sort();
  const sortedTeamNames = Object.keys(groupedByTeam).sort();

  const formatNamesWithTimes = (users, maxShow = 4) => {
    const visibleUsers = users.slice(0, Math.min(maxShow, users.length));
    const remaining = users.length - visibleUsers.length;
    return (
      <>
        {visibleUsers.map((user, index) => (
          <span key={user.uid}>
            {user.userName}
            <span style={{ color: 'var(--branding-app-surface-panel-hint)', fontWeight: '400' }}>
              {' '}({formatTime(user.timestamp)})
            </span>
            {index < visibleUsers.length - 1 && ', '}
          </span>
        ))}
        {remaining > 0 && (
          <span style={{ color: 'var(--branding-app-surface-panel-hint)', fontWeight: '400' }}>
            {' '}+{remaining} other{remaining > 1 ? 's' : ''}
          </span>
        )}
      </>
    );
  };

  if (onSite.length === 0 && offSite.length === 0) {
    return (
      <div style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--branding-app-surface-panel-hint)', fontSize: '14px' }}>
        No one on site
      </div>
    );
  }

  if (onSite.length === 0) {
    return (
      <div style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--branding-app-surface-panel-hint)', fontSize: '14px' }}>
        No one on site
      </div>
    );
  }

  return (
    <div style={{ height: '100%', background: 'transparent', padding: '8px', display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden' }}>
      <div style={{ marginBottom: '12px', flexShrink: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--branding-app-surface-panel-fg)', marginBottom: '2px', lineHeight: '1.3' }}>
          {onSite.length} On-Site
        </div>
        <div style={{ fontSize: '12px', color: 'var(--branding-app-surface-panel-sub)', lineHeight: '1.3' }}>
          {sortedTeamNames.length + sortedCompanyNames.length} group{(sortedTeamNames.length + sortedCompanyNames.length) !== 1 ? 's' : ''} active
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {sortedCompanyNames.map(companyName => (
          <div key={companyName} style={{ background: 'var(--branding-app-surface-panel)', borderRadius: '6px', padding: '8px', border: '1px solid var(--branding-app-surface-panel-border)' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--branding-app-surface-panel-muted)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a', flexShrink: 0 }} />
              {companyName} ({groupedByCompany[companyName].length})
            </div>
            <div style={{ fontSize: '12px', color: 'var(--branding-app-surface-panel-fg)', paddingLeft: '12px', lineHeight: '1.6' }}>
              {formatNamesWithTimes(groupedByCompany[companyName])}
            </div>
          </div>
        ))}

        {sortedTeamNames.map(teamName => (
          <div key={teamName} style={{ background: 'var(--branding-app-surface-panel)', borderRadius: '6px', padding: '8px', border: '1px solid var(--branding-app-surface-panel-border)' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--branding-app-surface-panel-muted)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a', flexShrink: 0 }} />
              {teamName} ({groupedByTeam[teamName].length})
            </div>
            <div style={{ fontSize: '12px', color: 'var(--branding-app-surface-panel-fg)', paddingLeft: '12px', lineHeight: '1.6' }}>
              {formatNamesWithTimes(groupedByTeam[teamName])}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AttendancePreview;
